import { useQuery } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmployeeContext } from '@/hooks/useEmployeeContext';
import { formatDateTime, formatStatusLabel, statusBadgeClass } from '@/lib/utils';
import { getInvitations } from '@/services/api.client';
import type { Invitation } from '@/types';

function StatCard({ count, label, to }: { count: number; label: string; to: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col gap-1 rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] p-4 hover:bg-[var(--bg-surface-raised)] transition-colors"
    >
      <span className="text-2xl font-bold text-[var(--text-accent)]">{count}</span>
      <span className="text-xs text-[var(--text-secondary)]">{label}</span>
    </Link>
  );
}

function InvitationRow({ inv }: { inv: Invitation }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-[var(--bg-surface-raised)] last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)] truncate">{inv.visitorName}</p>
        <p className="text-xs text-[var(--text-tertiary)]">{formatDateTime(inv.validFrom)}</p>
      </div>
      <Badge className={statusBadgeClass(inv.status)}>{formatStatusLabel(inv.status)}</Badge>
    </div>
  );
}

export function EmployeeHomePage() {
  const ctx = useEmployeeContext();

  const activeQuery = useQuery({
    queryKey: ['invitations', ctx?.propertyId, ctx?.unitId, { status: 'active', limit: 5 }],
    queryFn: () => (ctx ? getInvitations(ctx.propertyId, { status: 'active', limit: 5 }) : null),
    enabled: !!ctx,
  });

  const activeRes = activeQuery.data;

  const { data: allRes } = useQuery({
    queryKey: ['invitations', ctx?.propertyId, ctx?.unitId, { limit: 1 }],
    queryFn: () => (ctx ? getInvitations(ctx.propertyId, { limit: 1 }) : null),
    enabled: !!ctx,
  });

  const activeCount = activeRes?.meta.total ?? 0;
  const totalCount = allRes?.meta.total ?? 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <StatCard count={activeCount} label="Active Invitations" to="/employee/visitors" />
        <StatCard count={totalCount} label="Past Visits" to="/employee/history" />
      </div>

      <Link
        to="/employee/visitors?create=1"
        aria-label="Invite Visitor"
        className="flex items-center justify-center gap-2 rounded-xl py-4 px-3 text-sm font-medium text-[var(--text-inverse)] transition-opacity hover:opacity-90 w-full"
        style={{ background: 'var(--cta-primary-bg)' }}
      >
        <UserPlus className="size-5" />
        Invite Visitor
      </Link>

      <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bg-surface-raised)]">
          <p className="text-sm font-semibold text-[var(--text-primary)]">Recent Invitations</p>
          <Link
            to="/employee/visitors"
            className="text-xs text-[var(--text-accent)] hover:underline"
          >
            View all →
          </Link>
        </div>
        <div className="px-4 py-1">
          <QueryStateBoundary
            query={activeQuery}
            errorTitle="Couldn't load invitations"
            loadingFallback={
              <div className="space-y-3 py-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-3.5 w-36" />
                ))}
              </div>
            }
            emptyMessage="Nothing to show."
            isEmpty={(d) => (d?.data ?? []).length === 0}
          >
            {(res) => (
              <>
                {(res.data ?? []).map((inv) => (
                  <InvitationRow key={inv.id} inv={inv} />
                ))}
              </>
            )}
          </QueryStateBoundary>
        </div>
      </div>
    </div>
  );
}
